import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Grid, Pagination, CircularProgress, Typography } from '@mui/material';
import { obter_produtos } from '../services/prods-api.js';
import CartDrawer from './CartDrawer.tsx';
import CardProduct from './CardProduct.tsx';
import { useAuth } from '../context/AuthContext.tsx';

interface ProductsGridProps {
  searchTerm: string;
  grupoFilter: string;
  subgrupoFilter: string;
  divisaoFilter: string;
};

interface PaginationData {
  paginaAtual: number;
  totalPaginas: number;
  totalItens: number;
  itensPorPagina: number;
};

const ProductsGrid: React.FC<ProductsGridProps> = ({ searchTerm, grupoFilter, subgrupoFilter, divisaoFilter }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lista_produtos, setListaProdutos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    paginaAtual: 1,
    totalPaginas: 1,
    totalItens: 0,
    itensPorPagina: 12
  });

  const { userData } = useAuth();

  const carregarProdutos = useCallback(
    async (cnpj_cpf, search = '', grupo = '', subgrupo = '', divisao = '', page = 1, pageSize = 12) => {
    setLoading(true);
    try {
      const response = await obter_produtos(cnpj_cpf, search, grupo, subgrupo, divisao, page, pageSize); 
      setListaProdutos(response.produtos);         
      
      if (response.paginacao) {
        setPagination(response.paginacao);
      }    
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    } finally {
      setTimeout(setLoading(false), 200);
    }
  }, []);

  useEffect(() => {
    // Resetar para a primeira página quando os filtros mudarem
    setPagination(prev => ({...prev, paginaAtual: 1}));
  }, [searchTerm, grupoFilter, subgrupoFilter, divisaoFilter]);

  useEffect(() => {
    // Carregar os produtos ao mudar a página ou os filtros
    const produtos_cpf_cnpj = userData.clI_CNPJCPF;
    carregarProdutos(
      produtos_cpf_cnpj, 
      searchTerm, 
      grupoFilter, 
      subgrupoFilter, 
      divisaoFilter, 
      pagination.paginaAtual, 
      pagination.itensPorPagina
    );
  }, [
    searchTerm, 
    grupoFilter, 
    subgrupoFilter, 
    divisaoFilter, 
    pagination.paginaAtual, 
    carregarProdutos, 
    userData
  ]);

  useEffect(() => {
    window.history.pushState({ drawer: true }, '');

    const BackOnMobile = (event: PopStateEvent) => {
      if(drawerOpen){
        setDrawerOpen(false);
        navigate(location.pathname, { replace: true });
      }
    };

    window.addEventListener('popstate', BackOnMobile);
    return () => {
      window.removeEventListener('popstate', BackOnMobile);
    };
  },[drawerOpen, navigate, location.pathname]);

  const handleAddToCart = () => {
    setDrawerOpen(true); 
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setPagination(prev => ({...prev, paginaAtual: page}));
  };

  return (
    <>
      <CartDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <Grid container spacing={3.5} marginLeft={{ xs: 0, sm: 0.5, md: 1 }}> 
        {loading ? (
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Grid>
        ) : lista_produtos.length > 0 ? (
          <>
            {lista_produtos.map((item, index) => (
              <Grid item xs={12} sm={6} md={6} key={index}> 
                <CardProduct product={item} onAddToCart={handleAddToCart} />
              </Grid>
            ))}
          </>
        ) : (
          <Grid item xs={12} sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body1">Nenhum produto encontrado.</Typography>
          </Grid>
        )}
      </Grid>
      
      {!loading && pagination.totalPaginas > 1 && (
        <Grid container justifyContent="center" mt={4}>
          <Pagination
            count={pagination.totalPaginas}
            page={pagination.paginaAtual}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Grid>
      )}
    </>
  );
};

export default ProductsGrid;